import React, { useEffect, useMemo, useRef } from "react";
import { Sprite } from "@pixi/react";
import * as PIXI from "pixi.js";
import { makeTextureShadow } from "../../../../utils/buttonShadow";

/**
 * ButtonShadow — the round UI buttons' soft drop shadow (2026-10-05).
 *
 * Put it right BEFORE the button's own sprite, in the same container. It
 * draws the shadow of `texture`'s outline (the button's background — circle,
 * pill or rounded rectangle; never its icon glyph) at `width` x `height`, the
 * button's base display size, ONCE per size — and every frame copies the
 * button sprite's position, size, anchor, alpha and visibility from `target`,
 * so it follows a press bounce, a menu's grow-in or a hidden state.
 *
 * How it looks (dark, soft, far) is BUTTON_SHADOW in utils/buttonShadow — one
 * place for every button.
 *
 * @param {object} p
 * @param {React.RefObject} p.target  the button's sprite
 * @param {PIXI.Texture}    p.texture the texture whose outline casts it
 * @param {number}          p.width   the button's base display width
 * @param {number}          p.height  the button's base display height
 */
const ButtonShadow = ({ target, texture, width, height }) => {
  const ref = useRef(null);
  const resolution = window.devicePixelRatio || 1;
  const shadow = useMemo(
    () => makeTextureShadow(texture, width, height, resolution),
    [texture, width, height, resolution],
  );
  useEffect(() => () => shadow?.texture.destroy(true), [shadow]);

  useEffect(() => {
    if (!shadow) return undefined;
    const follow = () => {
      const s = ref.current;
      if (!s || s.destroyed) return;
      const t = target?.current;
      if (!t || t.destroyed) {
        s.visible = false;
        return;
      }
      s.visible = t.visible !== false && t.renderable !== false;
      s.alpha = t.alpha;
      s.position.set(t.x, t.y);
      s.rotation = t.rotation;
      // The button's CURRENT size against the base the shadow was drawn at.
      s.scale.set(Math.abs(t.width) / width || 0, Math.abs(t.height) / height || 0);
      // Its anchor, mapped into the padded shadow texture.
      const ax = t.anchor?.x ?? 0.5;
      const ay = t.anchor?.y ?? 0.5;
      s.anchor.set((shadow.pad + ax * width) / shadow.w, (shadow.pad + ay * height) / shadow.h);
    };
    follow();
    PIXI.Ticker.shared.add(follow);
    return () => PIXI.Ticker.shared.remove(follow);
  }, [shadow, target, width, height]);

  if (!shadow) return null;
  return (
    <Sprite ref={ref} texture={shadow.texture} name="button_shadow" eventMode="none" />
  );
};

export default ButtonShadow;
